import React from 'react'
import { produce } from 'immer'
import Input from 'src/components/base/Input'
import MultiSelectInput from 'src/components/base/MultiSelectInput'
import Button from 'src/components/base/Button'
import ExpandedTable from 'src/components/base/ExpandedTable'
import SelectInput from 'src/components/base/SelectInput'
import Checkbox from 'src/components/base/Checkbox'
import { Config } from 'src/types'

import { hoursToMs, msToHours } from './utils'

import './TerminalOperations.scss'

// Display field options shown on the visit card
const VISIT_CARD_DISPLAY_FIELD_OPTIONS = ['origin', 'destination', 'product', 'orderNumber']

export type TPropsTerminalOperations = {
  config: Config
  setConfig: React.Dispatch<React.SetStateAction<Config>>
}

function TerminalOperations({ config, setConfig }: TPropsTerminalOperations) {
  return (
    <div className="terminal-operations">
      <div className="topbar">
        <div className="topbar-section">
          <h2>Terminal operations</h2>
        </div>
      </div>
      <div className="content">
        <form>
          <div className="tq-form">
            <div className="tq-form-group">
              <Input
                label="Bollard length"
                type="number"
                required
                postfix="meter"
                placeholder="0"
                value={config?.bollardLength}
                onChange={e => {
                  const value = Number(e.target.value)
                  setConfig(
                    produce(draft => {
                      draft.bollardLength = value
                    })
                  )
                }}
              />
              <Input
                label="ETA Late Threshold"
                type="number"
                required
                postfix="hours"
                placeholder="0"
                value={msToHours(config?.etaTooLateThreshold)}
                onChange={e => {
                  const value = Number(e.target.value)
                  setConfig(
                    produce(draft => {
                      draft.etaTooLateThreshold = hoursToMs(value)
                    })
                  )
                }}
              />
            </div>
            <div className="tq-form-group">
              <MultiSelectInput
                label="Origin locations"
                options={config?.originLocations ?? []}
                selectedOptions={config?.originLocations ?? []}
                enableCustomOption
                onSelectedOptionsChange={newOriginLocations =>
                  setConfig(
                    produce(draft => {
                      draft.originLocations = newOriginLocations
                    })
                  )
                }
                placeholder="Add origin location"
              />
              <MultiSelectInput
                label="Destination locations"
                options={config?.destinationLocations ?? []}
                selectedOptions={config?.destinationLocations ?? []}
                enableCustomOption
                onSelectedOptionsChange={newDestinationLocations =>
                  setConfig(
                    produce(draft => {
                      draft.destinationLocations = newDestinationLocations
                    })
                  )
                }
                placeholder="Add destination location"
              />
              <MultiSelectInput
                label="Display fields for Visit Card"
                options={VISIT_CARD_DISPLAY_FIELD_OPTIONS}
                selectedOptions={config?.cardDisplayFields ?? []}
                onSelectedOptionsChange={newCardDisplayFields =>
                  setConfig(
                    produce(draft => {
                      draft.cardDisplayFields = newCardDisplayFields
                    })
                  )
                }
                placeholder="Add display field"
              />
            </div>
          </div>
        </form>
        <ExpandedTable emptyMessage="No non working hours defined" empty={config?.nonWorkingHours.length === 0}>
          <ExpandedTable.Header>
            <ExpandedTable.Title>Non working hours</ExpandedTable.Title>
            <ExpandedTable.Actions>
              <Button
                variant="brand-tertiary"
                icon="fa fa-plus"
                onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setConfig(
                    produce(draft => {
                      draft.nonWorkingHours.push({
                        startDay: 'Monday',
                        hour: 0,
                        minutes: 0,
                        duration: 0,
                        affectTerminalVisits: false
                      })
                    })
                  )
                }}
              >
                Add non working hours
              </Button>
            </ExpandedTable.Actions>
          </ExpandedTable.Header>
          <ExpandedTable.Body>
            <ExpandedTable.Row>
              <ExpandedTable.Cell asHeader style={{ width: '25%' }}>
                Start day
              </ExpandedTable.Cell>
              <ExpandedTable.Cell asHeader style={{ width: '20%' }}>
                Start time (hour:minutes)
              </ExpandedTable.Cell>
              <ExpandedTable.Cell asHeader style={{ width: '20%' }}>
                Duration (hours)
              </ExpandedTable.Cell>
              <ExpandedTable.Cell asHeader style={{ width: '30%' }}>
                Affect terminal visits
              </ExpandedTable.Cell>
              <ExpandedTable.Cell asHeader style={{ width: '5%' }} />
            </ExpandedTable.Row>
            {config?.nonWorkingHours.map((nonWorkingHour, index) => {
              const hourStart = nonWorkingHour.hour.toString().padStart(2, '0')
              const minuteStart = nonWorkingHour.minutes.toString().padStart(2, '0')
              return (
                <ExpandedTable.Row key={index}>
                  <ExpandedTable.Cell style={{ width: '25%' }}>
                    <SelectInput
                      options={['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']}
                      selectedOption={nonWorkingHour.startDay}
                      flexibleWidth
                      onSelectedOptionChange={option =>
                        setConfig(
                          produce(draft => {
                            draft.nonWorkingHours[index].startDay = option
                          })
                        )
                      }
                    />
                  </ExpandedTable.Cell>
                  <ExpandedTable.Cell style={{ width: '20%' }}>
                    <Input
                      type="time"
                      value={`${hourStart}:${minuteStart}`}
                      flexibleWidth
                      onChange={e => {
                        const [hour, minute] = e.target.value.split(':')
                        setConfig(
                          produce(draft => {
                            draft.nonWorkingHours[index].hour = Number(hour)
                            draft.nonWorkingHours[index].minutes = Number(minute)
                          })
                        )
                      }}
                    />
                  </ExpandedTable.Cell>
                  <ExpandedTable.Cell style={{ width: '20%' }}>
                    <Input
                      type="number"
                      value={msToHours(nonWorkingHour.duration)}
                      flexibleWidth
                      onChange={e => {
                        const value = Number(e.target.value)
                        setConfig(
                          produce(draft => {
                            draft.nonWorkingHours[index].duration = hoursToMs(value)
                          })
                        )
                      }}
                      postfix="hours"
                    />
                  </ExpandedTable.Cell>
                  <ExpandedTable.Cell style={{ width: '30%' }}>
                    <Checkbox
                      checked={nonWorkingHour.affectTerminalVisits}
                      onChange={e => {
                        const checked = e.target.checked
                        setConfig(
                          produce(draft => {
                            draft.nonWorkingHours[index].affectTerminalVisits = checked
                          })
                        )
                      }}
                      label="Respect working hours in planning"
                    />
                  </ExpandedTable.Cell>
                  <ExpandedTable.Cell className="align-right" style={{ width: '5%' }}>
                    <Button
                      variant="neutral-tertiary"
                      icon="fa fa-trash"
                      onClick={() => {
                        setConfig(
                          produce(draft => {
                            draft.nonWorkingHours.splice(index, 1)
                          })
                        )
                      }}
                    />
                  </ExpandedTable.Cell>
                </ExpandedTable.Row>
              )
            })}
          </ExpandedTable.Body>
        </ExpandedTable>
      </div>
    </div>
  )
}

export default TerminalOperations
